import { api } from '~/data/api'
import {
  ICompany,
  IInspection,
  IRequestParams,
  ISubmissionValues,
  IUser,
  ListInspectionPagination,
  TInspectionStatus,
} from '~/utils/types'
import { TDateInterval } from '../pages/list-inspection/components/DateIntervalMenu/DateIntervalMenu'

export interface IInspectionPaginationProps {
  page: number
  limit: number
  dateInterval?: TDateInterval | null
  search?: string
  lean?: boolean
}

export interface IUpdateInspectionStatusProps {
  inspection_id?: string
  inspection_status: TInspectionStatus
}

export async function updateInspectionStatus({
  inspection_id,
  inspection_status,
}: IUpdateInspectionStatusProps) {
  const response = await api().put(`/inspection/${inspection_id}`, {
    status: inspection_status,
  })
  return response.data
}

export async function createInspection(inspection: IInspection) {
  const response = await api().post(`/inspection`, inspection)
  return response.data
}

export interface IUpdateInspectionPayload {
  general_info: {
    operator?: string | null
    inspector?: string | null
    country?: string | null

    contact_info?: string | null
    region?: string | null
    due_date?: string | null
    po_number?: string | null
    po_item?: string | null
    project?: string | null
    inspection_number?: string | null
    aircraft_register?: string | null
    yom?: string | null
  }
  status?: string
}

export async function getInspection(inspection_id: string) {
  const response = await api().get(`/inspection/${inspection_id}`)
  return response.data
}

export interface IUpdateInspectionRequest {
  inspection_id?: string
  inspection: IUpdateInspectionPayload
  updateReport?: boolean
}
export async function updateInspection({
  inspection_id,
  inspection,
  updateReport,
}: IUpdateInspectionRequest) {
  const response = await api().put(`/inspection/${inspection_id}`, inspection, {
    params: {
      update_report: updateReport || undefined,
    },
  })
  return response.data
}

export async function cancelInspection(inspection_id: string) {
  const response = await api().delete(`/inspection/${inspection_id}`)
  return response.data
}

export interface IRegisterSubmissionProps {
  inspection_id?: string
  submission_values: {
    [index: string]: any
  }
}

export async function registerSubmission({
  inspection_id,
  submission_values,
}: IRegisterSubmissionProps) {
  const response = await api().post(
    `/inspection/${inspection_id}/register_submission`,
    submission_values,
  )
  return response.data
}

export interface IUpdateSubmission {
  reportId?: string | null
  submissionValues?: ISubmissionValues | null
  lockDateOfAudit?: boolean
}

export async function updateReportSubmission({
  reportId,
  submissionValues,
  lockDateOfAudit,
}: IUpdateSubmission) {
  if (!reportId || !submissionValues) return

  const response = await api().put(`/report/update_submission/${reportId}`, submissionValues, {
    params: {
      lockDateOfAudit,
    },
  })
  return response.data
}

export async function sendReportToRevision(reportId: string) {
  const response = await api().post(`/report/send_to_revision/${reportId}`)
  return response.data
}

export interface IReviewSubmission {
  reportId?: string
  status?: 'approve' | 'deny'
}

export async function reviewSubmission({ reportId, status = 'deny' }: IReviewSubmission) {
  const response = await api().post(`/report/review_submission/${reportId}`, {
    status,
  })
  return response.data
}

export interface IGetAircraftHistoryByAircraftId {
  aircraftId: string
  params?: IRequestParams
}

export async function getAircraftHistoryByAircraftId({
  aircraftId,
  params,
}: IGetAircraftHistoryByAircraftId) {
  const parsedParams = params && new URLSearchParams(params).toString()

  const parsedUrl = `aircraft_history/find_by_aircraft_id/${aircraftId}${
    parsedParams ? '?' + parsedParams : ''
  }`
  const response = await api().get(parsedUrl)
  return response.data
}

export interface IUploadReportFileProps {
  inspectionId: string
  reportFile: File
}
export interface IUploadInspectionThumbnail {
  inspectionId: string
  photo: File
}

export const uploadReportFile = async ({
  inspectionId,
  reportFile,
}: IUploadReportFileProps): Promise<void> => {
  const formData = new FormData()
  formData.append('file', reportFile)
  const response = await api().post(`/inspection/${inspectionId}/upload_report_file`, formData)
  return response.data
}

export type TablePrefillingSyncStrategy = 'skip' | 'replace' | 'append'

export interface ITablePrefillingOverride {
  table_id: string
  strategy: TablePrefillingSyncStrategy
}

export interface ISyncChecklistBody {
  table_prefilling_strategy?: TablePrefillingSyncStrategy
  table_prefilling_overrides?: ITablePrefillingOverride[]
}

export const syncInspectionChecklist = async (
  inspectionId: string,
  body?: ISyncChecklistBody,
) => {
  const response = await api().post(`/inspection/${inspectionId}/sync_checklist`, body)
  return response.data
}

export const listInspections = async (
  props: IInspectionPaginationProps,
): Promise<ListInspectionPagination> => {
  const response = await api().get(`/inspection`, {
    params: props,
  })
  return response.data
}

export const getAircraftImageByInspectionId = async (
  inspectionId: string,
  original?: string,
): Promise<string> => {
  const response = await api().get(`/inspection/${inspectionId}/aircraft_photo`, {
    params: {
      original,
    },
  })

  return response.data
}

export const searchAdditionalCompany = async (searchTerm: string): Promise<ICompany | null> => {
  const response = await api().get(`/company/search`, {
    params: {
      query: searchTerm,
    },
  })

  return response.data.company
}

export async function uploadInspectionThumbnail({
  inspectionId,
  photo,
}: IUploadInspectionThumbnail) {
  const formData = new FormData()
  formData.append('inspection_photo', photo)

  return await api().put(`/inspection/${inspectionId}/import_photo`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

export enum SOURCE_THUMBNAIL {
  INSPECTION = 'inspection',
  CHECKLIST = 'checklist',
  COMPANY = 'company',
  AIRCRAFT = 'aircraft',
}

export const getInspectionThumbnailByInspectionId = async (
  inspectionId: string,
): Promise<{
  image_source: SOURCE_THUMBNAIL
  file_url: string
}> => {
  const response = await api().get(`/inspection/${inspectionId}/thumbnail_picture`)

  return response.data
}

export interface IDeleteInspectionImage {
  inspectionId: string
}
export async function deleteInspectionImage({ inspectionId }: IDeleteInspectionImage) {
  return await api().delete(`/inspection/${inspectionId}/delete_photo`)
}

export interface IDiscrepancyReport {
  closed: number
  open: number
}

export interface IAuditItemReport {
  total: number
  answered: number
}

export interface IDashboardInspectionInformation {
  physical_findings: IDiscrepancyReport
  technical_findings: IDiscrepancyReport
  closed_percent: number
  audit_items: IAuditItemReport
  component_items: IAuditItemReport
  general_info: IAuditItemReport
  physical_items: IAuditItemReport
  tracking_items: IAuditItemReport
  users: IUser[]
}

export async function getDashboardInspectionInformation(
  inspectionId: string,
): Promise<IDashboardInspectionInformation> {
  const response = await api().get(`/inspection/${inspectionId}/overview_dashboard`)
  return response.data
}
